import { useCallback, useRef, useState } from 'react';

import searchObject from '../utils/searchObject/index.ts';
import useCache from '../utils/useCache/index.ts';
import useEventCallback from '../utils/useEventCallback/index.ts';

const noop = () => false;

type UserSearchOptions = {
  excludeFromSearch?: (key: string) => boolean;
  debounce?: number;
  onChange?: (key: string) => void;
};

function useSearch(
  initialValue = '',
  {
    excludeFromSearch = noop,
    debounce = 100,
    onChange: onChangeFromProps = noop
  }: UserSearchOptions = {}
) {
  const [query, setQuery] = useState(initialValue);
  const currentSearchValue = useRef(query);
  const exclude = useCache(excludeFromSearch);
  const search = useCallback(
    (item: Record<string, unknown>) =>
      searchObject(query, { excludeFromSearch: exclude.current })(item),
    [query, exclude]
  );
  const cachedOnChange = useEventCallback(onChangeFromProps);
  const onChange = useCallback(
    (value: string) => {
      currentSearchValue.current = value;
      setTimeout(() => {
        if (currentSearchValue.current === value) {
          setQuery(value);
          cachedOnChange(value);
        }
      }, debounce);
    },
    [setQuery, cachedOnChange, debounce]
  );

  return {
    isSearching: Boolean(query),
    query,
    setQuery,
    search,
    onChange,
    currentSearchValue
  };
}

export default useSearch;
